<template>
  <b-modal
    :visible="visible"
    :title="$t('filters')"
    hide-footer
    @hidden="$emit('close')"
    centered
  >
    <div class="case-filter-modal">
      <div class="form-group mb-2">
        <label class="input-label form-label mb-1">{{ $t("createdFrom") }}</label>
        <input
          type="date"
          class="form-control"
          v-model="localFrom"
        />
      </div>
      <div class="form-group mb-2">
        <label class="input-label form-label mb-1">{{ $t("createdTo") }}</label>
        <input
          type="date"
          class="form-control"
          v-model="localTo"
        />
      </div>
      <div class="form-group mb-2">
        <label class="input-label form-label mb-1">{{ $t("filterByFlag") }}</label>
        <select class="form-control" v-model="localSelectedFilter">
          <option value="all">{{ $t("all") }}</option>
          <option
            v-for="flag in existingFlags"
            :key="flag.id"
            :value="flag.id"
          >
            {{ flag.name }}
          </option>
        </select>
      </div>
      <div class="form-group mb-2">
        <label class="input-label form-label">{{ $t("company") }}</label>
        <MultiSelectInput
          v-model="localCompanySelection"
          :options="customers"
          :query="{ isMultiSelect: true }"
          label="companyName"
          trackBy="id"
          moduleName="customers"
          :multiple="true"
        >
          <template #option="{ props }">
            <div>
              <p>
                {{ props.option.companyNumber }} -
                {{ props.option.companyName }}
              </p>
            </div>
          </template>
        </MultiSelectInput>
      </div>
      <div class="form-group mb-2">
        <label class="input-label form-label">{{ $t("productType") }}</label>
        <multi-select
          v-model="localProductTypeSelection"
          :options="productTypeOptions"
          :custom-label="productTypeLabel"
          track-by="id"
          :multiple="true"
          :internal-search="true"
          :placeholder="$t('selectOption')"
        />
      </div>
      <div class="form-group mb-2">
        <label class="input-label form-label mb-1">{{ $t("needsManualApprovement") }}</label>
        <select class="form-control" v-model="localNeedsManualApprovalFilter">
          <option value="all">{{ $t("all") }}</option>
          <option value="notStarted">{{ $t("notYetStarted") }}</option>
        </select>
      </div>
      <div class="form-group mb-2">
        <label class="input-label form-label mb-1">{{ $t("marginLine") }}</label>
        <select class="form-control" v-model="localMarginLineFilter">
          <option value="all">{{ $t("all") }}</option>
          <option value="set">{{ $t("marginLineSet") }}</option>
          <option value="notSet">{{ $t("marginLineNotSet") }}</option>
        </select>
      </div>
      <div class="d-flex flex-column gap-2">
        <button class="btn btn-primary w-100" @click="handleApply">
          {{ $t("apply") }}
        </button>
        <button class="btn btn-outline-secondary w-100" @click="handleClear">
          {{ $t("clear") }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal } from "bootstrap-vue";
import MultiSelect from "vue-multiselect";
import MultiSelectInput from "@/components/MultiSelectInput.vue";

export default {
  components: {
    BModal,
    MultiSelect,
    MultiSelectInput,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    from: {
      type: [String, null],
      default: null,
    },
    to: {
      type: [String, null],
      default: null,
    },
    selectedFilter: {
      type: String,
      default: "all",
    },
    existingFlags: {
      type: Array,
      default: () => [],
    },
    companyIds: {
      type: Array,
      default: () => [],
    },
    customers: {
      type: Array,
      default: () => [],
    },
    needsManualApprovalFilter: {
      type: String,
      default: "all",
    },
    marginLineFilter: {
      type: String,
      default: "all",
    },
    productTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localFrom: this.from,
      localTo: this.to,
      localSelectedFilter: this.selectedFilter,
      localCompanySelection: [],
      localProductTypeSelection: [],
      localNeedsManualApprovalFilter: this.needsManualApprovalFilter,
      localMarginLineFilter: this.marginLineFilter,
      productTypeOptions: [
        { id: "dentaBridge", i18nKey: "dentaBridge" },
        { id: "dentaTemp", i18nKey: "shellTempDesign" },
        { id: "dentaWax", i18nKey: "setupGuideForImplants" },
      ],
    };
  },
  watch: {
    from(newVal) {
      this.localFrom = newVal;
    },
    to(newVal) {
      this.localTo = newVal;
    },
    visible(newVal) {
      if (newVal) {
        this.localFrom = this.from;
        this.localTo = this.to;
        this.localSelectedFilter = this.selectedFilter;
        this.localNeedsManualApprovalFilter = this.needsManualApprovalFilter;
        this.localMarginLineFilter = this.marginLineFilter;
        this.syncCompanySelection();
        this.syncProductTypeSelection();
      }
    },
    selectedFilter(newVal) {
      this.localSelectedFilter = newVal;
    },
    needsManualApprovalFilter(newVal) {
      this.localNeedsManualApprovalFilter = newVal;
    },
    marginLineFilter(newVal) {
      this.localMarginLineFilter = newVal;
    },
    companyIds: {
      handler() {
        this.syncCompanySelection();
      },
      immediate: true,
    },
    customers: {
      handler() {
        this.syncCompanySelection();
      },
      immediate: true,
    },
    productTypes: {
      handler() {
        this.syncProductTypeSelection();
      },
      immediate: true,
    },
  },
  methods: {
    productTypeLabel(option) {
      return option ? this.$t(option.i18nKey) : "";
    },
    syncCompanySelection() {
      const ids = Array.isArray(this.companyIds) ? this.companyIds : [];
      const mapped = ids
        .map((id) =>
          (this.customers || []).find((customer) => customer.id === id)
        )
        .filter(Boolean);
      this.localCompanySelection = mapped;
    },
    syncProductTypeSelection() {
      const types = Array.isArray(this.productTypes) ? this.productTypes : [];
      const mapped = types
        .map((id) =>
          this.productTypeOptions.find((opt) => opt.id === id)
        )
        .filter(Boolean);
      this.localProductTypeSelection = mapped;
    },
    handleApply() {
      const companyIds = (this.localCompanySelection || []).map(
        (company) => company.id
      );
      const productTypes = (this.localProductTypeSelection || []).map(
        (opt) => opt.id
      );
      this.$emit("apply", {
        from: this.localFrom,
        to: this.localTo,
        selectedFilter: this.localSelectedFilter,
        companyIds,
        productTypes,
        needsManualApprovalFilter: this.localNeedsManualApprovalFilter,
        marginLineFilter: this.localMarginLineFilter,
      });
    },
    handleClear() {
      this.localFrom = null;
      this.localTo = null;
      this.localSelectedFilter = "all";
      this.localCompanySelection = [];
      this.localProductTypeSelection = [];
      this.localNeedsManualApprovalFilter = "all";
      this.localMarginLineFilter = "all";
      this.$emit("clear");
    },
  },
};
</script>

<style scoped>
.case-filter-modal .form-group {
  width: 100%;
}
</style>

